import { z } from 'zod';

import { foldDigitsToAscii } from '@shared/format';

/**
 * One schema file per feature, driving the resolver **and** the request shape.
 * The TypeScript type is derived with `z.infer`, never hand-written in parallel.
 *
 * Every message is Arabic and states the fix rather than the failure.
 */

/**
 * The role dialog.
 *
 * One control, and it is required — «مشترك» is the revoke, so the field has no
 * empty state that means anything. The value is validated as a non-empty string
 * rather than against a compiled member list: the registry is code-seeded
 * upstream and each service refuses an unknown name with its own error, which is
 * the check that actually binds.
 */
export const roleFormSchema = z.object({
  accountRole: z.string().min(1, 'اختر دوراً للحساب.'),
});

export type RoleFormValues = z.infer<typeof roleFormSchema>;

/**
 * The biography.
 *
 * **No character limit.** No catalog row declares one, and a counter would
 * promise a constraint the service does not enforce. What is enforced is that
 * the field is not emptied by accident: the section exists because the account
 * has public text, and saving a blank would delete it silently through a control
 * that says «احفظ».
 */
export const biographyFormSchema = z.object({
  biography: z.string().trim().min(1, 'اكتب النصّ الذي يظهر على الملفّ العامّ.'),
});

export type BiographyFormValues = z.infer<typeof biographyFormSchema>;

/**
 * The manual grant.
 *
 * One field: the expiry. The scope is not asked for — it is a platform setting,
 * and a control with one possible value reads as a question whose answer never
 * varies.
 *
 * The date is folded from Arabic-Indic before validation, so an operator typing
 * on an Arabic keyboard is never rejected for the glyph their keyboard produced,
 * and it is checked against the `YYYY-MM-DD` shape a `<input type="date">`
 * produces rather than parsed loosely — a value the browser did not produce is a
 * value the service cannot read.
 */
export const grantFormSchema = z.object({
  endsAt: z
    .string()
    .transform(foldDigitsToAscii)
    .pipe(
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'اختر تاريخ انتهاء للمنحة.')
        .refine((value) => !Number.isNaN(Date.parse(`${value}T00:00:00Z`)), 'هذا التاريخ غير صالح.'),
    ),
});

export type GrantFormInput = z.input<typeof grantFormSchema>;
export type GrantFormValues = z.infer<typeof grantFormSchema>;
